import type { GeoJSONFeature } from "mapbox-gl";
import MapboxViewer from "@/components/mapbox/MapboxViewer";
import { FeatureTypeEnum } from "@/types/enum";
import { MapboxPolygonObject } from "@/components/mapbox/Lib/Objects/MapboxPolygonObject";
import type SimShadyShadowLayer from "@/components/mapbox/Lib/Shading/SimShadyShadowLayer";
import {
  calculatePolygonSunHours,
  splitTargetAndShadingFeatures,
} from "@/components/mapbox/Lib/Shading/shadeProfile";

export const getMapboxObject = (
  feature: GeoJSONFeature,
  mapboxViewer: MapboxViewer
) => {
  let object = null;

  if (feature.properties?.category === FeatureTypeEnum.BUILDING) {
    object = mapboxViewer.entities.buildingEntity?.getById(
      feature.properties?.contentId!.toString()
    );
  } else if (feature.properties?.category === FeatureTypeEnum.POLYGON) {
    object = mapboxViewer.entities.polygonEntity?.getById(
      feature.properties?.contentId!.toString()
    );
  } else if (feature.properties?.category === "measurement_length") {
    object = mapboxViewer.entities.measureLengthEntity?.getById(
      feature.properties?.contentId!.toString()
    );
  } else if (feature.properties?.category === "measurement_area") {
    object = mapboxViewer.entities.measureAreaEntity?.getById(
      feature.properties?.contentId!.toString()
    );
  } else if (feature.properties?.category === "polygon-vertex") {
    // Return the parent polygon for vertex features
    object = mapboxViewer.entities.polygonEntity?.getById(
      feature.properties?.polygonId!.toString()
    );
    // Add vertex information to the object
    if (object instanceof MapboxPolygonObject) {
      object._selectedVertex = {
        type: feature.properties?.vertexType,
        index: feature.properties?.vertexIndex,
        insertIndex: feature.properties?.insertIndex,
        featureId: feature.id?.toString(),
      };
    }
  }

  return object;
};

export interface VertexInfo {
  type: "corner" | "midpoint";
  index?: number;
  insertIndex?: number;
  featureId: string | number | undefined;
}

export const pointInPolygon = (
  openRing: number[][],
  lng: number,
  lat: number
) => {
  let inside = false;
  for (let i = 0, j = openRing.length - 1; i < openRing.length; j = i++) {
    const [xi, yi] = openRing[i];
    const [xj, yj] = openRing[j];
    const intersect =
      yi > lat !== yj > lat && lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
};

export const getSunHours = async (
  shadeMap: SimShadyShadowLayer,
  openRing: number[][],
  stepLng: number = 0.00002,
  stepLat?: number,
  targetFeature?: GeoJSONFeature,
) => {
  const features = await shadeMap.getShadingFeatures();
  if (!targetFeature) {
    return calculatePolygonSunHours(features, openRing, stepLng, stepLat);
  }

  const { simulationFeatures, shadingFeatures } = splitTargetAndShadingFeatures(
    targetFeature,
    features,
  );

  return calculatePolygonSunHours(simulationFeatures, openRing, stepLng, stepLat, {
    simulationFeatures,
    shadingFeatures,
  });
};
